import type { ReactNode } from "react"
import type { DateRange } from "react-day-picker"

import type { SerializedDateRange } from "../../../typings"
import {
	DateAfterFilterModifier,
	DateBeforeFilterModifier,
	DateEqualsFilterModifier,
	DateNotEqualsFilterModifier,
	DateRangeInFilterModifier,
	DateRangeNotInFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { SubmittedAtFilterCell } from "./SubmittedAtFilterCell"

type SubmittedAtFieldValue = Date
type SubmittedAtFilterValue = Date | DateRange
type SerializedSubmittedAtFilterValue = string | SerializedDateRange

export class SubmittedAtFilterDef extends BaseFilterDef<
	SubmittedAtFieldValue,
	SubmittedAtFilterValue,
	SerializedSubmittedAtFilterValue
> {
	readonly id = "submitted_at"
	readonly defaultFilterValue: SubmittedAtFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: DateEqualsFilterModifier,
			isValueValid: (_value: SubmittedAtFieldValue): _value is Date => true,
			isFilterValueValid: (filterValue: SubmittedAtFilterValue): filterValue is Date =>
				filterValue instanceof Date,
		}),
		notEquals: createFilterModifierConfig({
			modifier: DateNotEqualsFilterModifier,
			isValueValid: (_value: SubmittedAtFieldValue): _value is Date => true,
			isFilterValueValid: (filterValue: SubmittedAtFilterValue): filterValue is Date =>
				filterValue instanceof Date,
		}),
		before: createFilterModifierConfig({
			modifier: DateBeforeFilterModifier,
			isValueValid: (_value: SubmittedAtFieldValue): _value is Date => true,
			isFilterValueValid: (filterValue: SubmittedAtFilterValue): filterValue is Date =>
				filterValue instanceof Date,
		}),
		after: createFilterModifierConfig({
			modifier: DateAfterFilterModifier,
			isValueValid: (_value: SubmittedAtFieldValue): _value is Date => true,
			isFilterValueValid: (filterValue: SubmittedAtFilterValue): filterValue is Date =>
				filterValue instanceof Date,
		}),
		in: createFilterModifierConfig({
			modifier: DateRangeInFilterModifier,
			isValueValid: (_value: SubmittedAtFieldValue): _value is Date => true,
			isFilterValueValid: (filterValue: SubmittedAtFilterValue): filterValue is DateRange =>
				!(filterValue instanceof Date) && "to" in filterValue && "from" in filterValue,
		}),
		notIn: createFilterModifierConfig({
			modifier: DateRangeNotInFilterModifier,
			isValueValid: (_value: SubmittedAtFieldValue): _value is Date => true,
			isFilterValueValid: (filterValue: SubmittedAtFilterValue): filterValue is DateRange =>
				!(filterValue instanceof Date) && "to" in filterValue && "from" in filterValue,
		}),
	}

	render(props: FilterDefRenderProps<SubmittedAtFilterDef>): ReactNode {
		return <SubmittedAtFilterCell key={props.filter.id} {...props} />
	}
}
